<script setup lang="ts">
//#region imports
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, requiredIf } from '@vuelidate/validators';
import { useApi } from '@/api';
import { useValidate } from '@/composables/useValidate';
import { useRulesValidator } from '@/composables/useRulesValidator';
import { useDictList } from '@/composables/useDictList';
import { usePopupManagerCache } from '@/composables/usePopupManagerCache';
import type {
  PostDomainsManagementPlatformServiceDomainsData,
  GetDomainsManagementPlatformServersResponse,
  PutDomainsManagementPlatformServiceDomainsByIdData,
} from '@/types/openapi-ts';
import type { EmitsToListeners } from '@/types/core';
import type { MergeUnion, MakeNullable } from '@/types/util';
//#endregion

//#region types
interface Props {
  domainId?: string | null,
  savedData?: any;
}

interface  Emits {
  'success': [],
  'close': [],
}

export interface ServiceDomainParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

type IPAddressItem = {
  id: string;
  name: string;
  count: number;
};

type MergedPostDomainsManagementPlatformServiceDomainsData = MergeUnion<PostDomainsManagementPlatformServiceDomainsData['body']>;

type FormType = Omit<MergedPostDomainsManagementPlatformServiceDomainsData,
  'id' | 'ns_servers' | 'pick_platform_server_type' | 'platform_server_id' | 'platform_server_ipv4_address_id' | 'domain_registrar_account_id' | 'dns_hosting_account_id'
> & {
  id?: string;
  ns_servers?: string;
  pick_platform_server_type?: MergedPostDomainsManagementPlatformServiceDomainsData['pick_platform_server_type'];
} & MakeNullable<MergedPostDomainsManagementPlatformServiceDomainsData,
  'platform_server_id' | 'platform_server_ipv4_address_id' | 'domain_registrar_account_id' | 'dns_hosting_account_id'
>;
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  domainId: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
const { isDomain, nsServers } = useRulesValidator();
const { autoManualList } = useDictList();
const { checkField, isInvalidField, isInvalidForm } = useValidate({
  rules: getRules(),
  form: computed(() => {
    return form.value || {};
  }),
});

const { fetchData } = usePopupManagerCache(props, async () => {
  return await Promise.all([
    fetchServiceOrigins(),
    fetchRegistrarAccounts(),
    fetchDNSHostingAccounts(),
  ]);
});
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref({});

const IPv4Addresses = ref<IPAddressItem[]>([]);
const IPv6Addresses = ref<IPAddressItem[]>([]);
const platformServersList = ref<GetDomainsManagementPlatformServersResponse['payload']>([]);

const dictionaries = ref<any>({
  serviceOrigins: [],
  registrarAccounts: [],
  dnsHostingAccounts: [],
});

const form = ref<FormType>({
  setup_mode: 'managed',
  platform_service_origin_id: '',
  domain_name: '',
  platform_server_id: null,
  platform_server_ipv4_address_id: null,
  platform_server_ipv6_address_id: null,
  ssl_issuance: 'auto',
  ns_management: 'auto',
  domain_registrar_account_id: null,
  ns_servers: '',
  dns_management: 'auto',
  dns_hosting_account_id: null,
});
//#endregion

//#region computed
const isManagedSetupMode = computed(() => {
  return form.value.setup_mode === 'managed';
});

const isEdit = computed(() => {
  return props.domainId;
});

const pPopupTitle = computed(() => {
  return `${t(`domainManagement.popup.${isEdit.value ? 'edit' : 'add'}`)} ${t('domainManagement.popup.serviceDomain')}`;
});
//#endregion

//#region methods
function getRules() {
  return {
    domain_name: {
      required,
      isDomain,
    },
    platform_service_origin_id: {
      required,
    },
    platform_server_id: {
      required: requiredIf(() => !!(isManagedSetupMode.value && (form.value.pick_platform_server_type === 'manual' || isEdit.value))),
    },
    platform_server_ipv4_address_id: {
      required: requiredIf(() => !!(isManagedSetupMode.value && (form.value.pick_platform_server_type === 'manual' || isEdit.value))),
    },
    domain_registrar_account_id: {
      required: requiredIf(() => isManagedSetupMode.value && form.value.ns_management === 'auto'),
    },
    dns_hosting_account_id: {
      required: requiredIf(() => isManagedSetupMode.value && form.value.dns_management === 'auto'),
    },
    ns_servers: {
      nsServers,
    },
  };
}

async function open() {

  if(isEdit.value) {
    const { data: { payload } } = await api.getServiceDomainById(props.domainId!);

    const {
      id,
      setup_mode,
      platform_service_origin_id,
      domain_name,
      platform_server_id,
      platform_server_ipv4_address_id,
      platform_server_ipv6_address_id,
      ssl_issuance,
      ns_management,
      domain_registrar_account_id,
      ns_servers,
      dns_management,
      dns_hosting_account_id,
    } = payload;

    form.value = {
      id,
      setup_mode,
      platform_service_origin_id,
      domain_name,
      platform_server_id,
      platform_server_ipv4_address_id,
      platform_server_ipv6_address_id,
      ssl_issuance,
      ns_management,
      domain_registrar_account_id,
      dns_management,
      dns_hosting_account_id,
    };

    form.value.ns_servers = ns_servers.join('\n');

    await fetchServers();
  } else {
    form.value.pick_platform_server_type = 'auto';
  }

  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function handleSave() {
  isLoading.value = true;
  errors.value = {};

  const parsedNs = form.value.ns_servers ? form.value.ns_servers.trim().split('\n').map((ns_server: string) => ns_server.trim()) : [];

  let promise;

  if(isEdit.value) {
    const payload = {
      ...form.value,
      ns_servers: parsedNs,
    } as PutDomainsManagementPlatformServiceDomainsByIdData['body'];
    promise = api.updateServiceDomain(form.value.id!, payload);
  } else {
    const payload = {
      ...form.value,
      ns_servers: parsedNs,
    } as PostDomainsManagementPlatformServiceDomainsData['body'];
    promise = api.createServiceDomain(payload);
  }

  promise.then(() => {
    emit('success');
    isVisible.value = false;
  }).catch((error) => {
    errors.value = error.data.errors;
  }).finally(() => {
    isLoading.value = false;
  });
}

async function fetchServers() {
  const { data: { payload: platformServers } } = await api.getPlatformServers({ limit: 1000 });
  platformServersList.value = platformServers;
  fetchIPs();
}

function fetchIPs() {
  const [selectedPlatformServer] = platformServersList.value
    .filter((platformServer) => platformServer.id === form.value.platform_server_id);
  const IPv4AddressesLocal: IPAddressItem[] = [];
  const IPv6AddressesLocal: IPAddressItem[] = [];
  if (selectedPlatformServer) {
    const {
      main_ipv4_address,
      main_ipv4_address_id,
      main_ipv4_address_used_count,
      additional_ipv4_addresses,
      main_ipv6_address,
      main_ipv6_address_id,
      main_ipv6_address_used_count,
      additional_ipv6_addresses,
    } = selectedPlatformServer;

    IPv4AddressesLocal.push(
      { id: main_ipv4_address_id!, name: main_ipv4_address, count: main_ipv4_address_used_count },
    );

    if (additional_ipv4_addresses.length) {
      IPv4AddressesLocal.push(
        ...additional_ipv4_addresses.map((item) => ({ id: item.id, name: item.ip_address, count: item.used_count })),
      );
    }

    if (main_ipv6_address_id) {
      IPv6AddressesLocal.push(
        { id: main_ipv6_address_id, name: main_ipv6_address!, count: main_ipv6_address_used_count },
      );
    }

    if (additional_ipv6_addresses.length) {
      IPv6AddressesLocal.push(
        ...additional_ipv6_addresses.map((item) => ({ id: item.id, name: item.ip_address, count: item.used_count })),
      );
    }
  }

  IPv4Addresses.value = IPv4AddressesLocal.sort((a, b) => Number(a.count) - Number(b.count));
  IPv6Addresses.value = IPv6AddressesLocal.sort((a, b) => Number(a.count) - Number(b.count));
}

function changeServer() {
  form.value.platform_server_ipv4_address_id = '';
  form.value.platform_server_ipv6_address_id = '';
}

async function fetchServiceOrigins() {
  const { data: { payload: serviceOrigins } } = await api.getServiceOrigins({});
  return serviceOrigins;
}

async function fetchRegistrarAccounts() {
  const { data: { payload: registrarAccounts } } = await api.getDomainRegistrarAccounts({ limit: 1000 });
  return registrarAccounts;
}

async function fetchDNSHostingAccounts() {
  const { data: { payload: dnsHostingAccounts } } = await api.getDNSHostingAccounts({ limit: 1000 });
  return dnsHostingAccounts;
}
//#endregion

//#region watch
watch(() => form.value.setup_mode, (value) => {
  if (value === 'manual') {
    form.value.domain_registrar_account_id = '';
  }
});

watch(() => form.value.dns_management, (value) => {
  if (value === 'manual') {
    form.value.dns_hosting_account_id = '';
  }
});

watch(() => form.value.pick_platform_server_type, (value) => {
  if (value === 'auto') {
    form.value.platform_server_id = '';
    form.value.platform_server_ipv4_address_id = '';
    form.value.platform_server_ipv6_address_id = null;
  } else {
    fetchServers();
  }
});

watch(() => form.value.platform_server_id, (value) => {
  if (value) {
    fetchIPs();
  }
});
//#endregion

//#region hooks
onBeforeMount(async () => {
  const [serviceOrigins, registrarAccounts, dnsHostingAccounts] = await fetchData();
  dictionaries.value = {
    serviceOrigins,
    registrarAccounts,
    dnsHostingAccounts,
  };
});
//#endregion

defineExpose({
  open,
  fetchData,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="service-domain-popup"
    :title="pPopupTitle"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiRadioGroup
          v-if="!isEdit"
          v-model="form.setup_mode"
          :label="$t('domainManagement.popup.form.setup_mode.label')"
          :options="[
            {
              label: $t('domainManagement.popup.form.setup_mode.managed'),
              value: 'managed',
            },
            {
              label: $t('domainManagement.popup.form.setup_mode.manual'),
              value: 'manual',
            }
          ]"
        />
        <UiInput
          v-if="!isEdit"
          v-model="form.domain_name"
          :placeholder="$t(`domainManagement.popup.form.domain_name.placeholder`)"
          :label="$t(`domainManagement.popup.form.domain_name.label`)"
          autosize
          required
          size="big"
          :is-error="isInvalidField('domain_name')"
          @blur="checkField('domain_name')"
        />
        <UiSelectElement
          v-if="!isEdit"
          v-model="form.platform_service_origin_id"
          :label="$t('domainManagement.popup.form.platform_service_origin_id.label')"
          :placeholder="$t('domainManagement.popup.form.platform_service_origin_id.placeholder')"
          :list="dictionaries.serviceOrigins"
          size="big"
          filterable
          required
          :is-error="isInvalidField('platform_service_origin_id')"
          @blur="checkField('platform_service_origin_id')"
        />
        <template v-if="isManagedSetupMode">
          <UiRadioGroup
            v-if="!isEdit"
            v-model="form.pick_platform_server_type"
            :label="$t('domainManagement.popup.form.pick_platform_server_type.label')"
            :options="autoManualList"
          />
          <UiSelectElement
            v-if="form.pick_platform_server_type === 'manual' || isEdit"
            v-model="form.platform_server_id"
            :label="$t('domainManagement.popup.form.platform_server_id.label')"
            :placeholder="$t('domainManagement.popup.form.platform_server_id.placeholder')"
            :list="platformServersList"
            size="big"
            filterable
            required
            :is-error="isInvalidField('platform_server_id')"
            @blur="checkField('platform_server_id')"
            @change="changeServer"
          />
          <UiSelectElement
            v-if="(form.pick_platform_server_type === 'manual' || isEdit) && form.platform_server_id"
            v-model="form.platform_server_ipv4_address_id"
            :label="$t('domainManagement.popup.form.platform_server_ipv4_address_id.label')"
            :placeholder="$t('domainManagement.popup.form.platform_server_ipv4_address_id.placeholder')"
            size="big"
            filterable
            required
            :is-error="isInvalidField('platform_server_ipv4_address_id')"
            @blur="checkField('platform_server_ipv4_address_id')"
          >
            <template v-slot:options>
              <ElOption
                v-for="IPv4Address in IPv4Addresses"
                :key="IPv4Address.id"
                :label="`${IPv4Address.name} (${IPv4Address.count})`"
                :value="IPv4Address.id"
              />
            </template>
          </UiSelectElement>
          <UiSelectElement
            v-if="(form.pick_platform_server_type === 'manual' || isEdit) && form.platform_server_id"
            v-model="form.platform_server_ipv6_address_id"
            :label="$t('domainManagement.popup.form.platform_server_ipv6_address_id.label')"
            :placeholder="$t('domainManagement.popup.form.platform_server_ipv6_address_id.placeholder')"
            size="big"
            filterable
            clearable
          >
            <template v-slot:options>
              <ElOption
                v-for="IPv6Address in IPv6Addresses"
                :key="IPv6Address.id"
                :label="`${IPv6Address.name} (${IPv6Address.count})`"
                :value="IPv6Address.id"
              />
            </template>
          </UiSelectElement>
          <UiRadioGroup
            v-model="form.ssl_issuance"
            :label="$t('domainManagement.popup.form.ssl_issuance.label')"
            :options="[
              {
                label: $t('domainManagement.popup.form.ssl_issuance.auto'),
                value: 'auto',
              },
              {
                label: $t('domainManagement.popup.form.ssl_issuance.manual'),
                value: 'manual',
              }
            ]"
          />
          <UiRadioGroup
            v-model="form.ns_management"
            :label="$t('domainManagement.popup.form.ns_management.label')"
            :options="[
              {
                label: $t('domainManagement.popup.form.ns_management.auto'),
                value: 'auto',
              },
              {
                label: $t('domainManagement.popup.form.ns_management.manual'),
                value: 'manual',
              }
            ]"
          />
          <UiSelectElement
            v-if="form.ns_management === 'auto'"
            v-model="form.domain_registrar_account_id"
            :label="$t('domainManagement.popup.form.domain_registrar_account_id.label')"
            :placeholder="$t('domainManagement.popup.form.domain_registrar_account_id.placeholder')"
            :list="dictionaries.registrarAccounts"
            size="big"
            filterable
            required
            :is-error="isInvalidField('domain_registrar_account_id')"
            @blur="checkField('domain_registrar_account_id')"
          />
          <UiTextarea
            v-model="form.ns_servers"
            :label="$t('domainManagement.popup.form.ns_servers.label')"
            :placeholder="$t(`domainManagement.popup.form.ns_servers.placeholder`)"
            :rows="3"
            :is-error="isInvalidField('ns_servers')"
            @blur="checkField('ns_servers')"
          />
          <UiRadioGroup
            v-model="form.dns_management"
            :label="$t('domainManagement.popup.form.dns_management.label')"
            :options="[
              {
                label: $t('domainManagement.popup.form.dns_management.auto'),
                value: 'auto',
              },
              {
                label: $t('domainManagement.popup.form.dns_management.manual'),
                value: 'manual',
              }
            ]"
          />
          <UiSelectElement
            v-if="form.dns_management === 'auto'"
            v-model="form.dns_hosting_account_id"
            :label="$t('domainManagement.popup.form.dns_hosting_account_id.label')"
            :placeholder="$t('domainManagement.popup.form.dns_hosting_account_id.placeholder')"
            :list="dictionaries.dnsHostingAccounts"
            size="big"
            filterable
            required
            :is-error="isInvalidField('dns_hosting_account_id')"
            @blur="checkField('dns_hosting_account_id')"
          />
        </template>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t(`domainManagement.popup.cancel`) }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="isInvalidForm || isLoading"
          @click="handleSave"
        >
          {{ $t(`domainManagement.popup.save`) }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
